// MediaViewer(APP#42)—— 全螢幕相片 / 影片檢視器。
//
// 相片:等比縮放呈現。影片:<video controls> + 「倍速」按鈕(0.5/1/1.5/2,循環切換,
// 直接設 video.playbackRate)。上一張 / 下一張導覽(鍵盤 ← →、Esc 關閉)。下載 / 刪除。
// 位元組永遠是本機 blob(useObjectUrl 依 id 取),零後端。

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRate, nextPlaybackRate, type GalleryMeta } from './galleryLib';
import { useObjectUrl } from './useGallery';

interface Props {
  items: GalleryMeta[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
}

export function MediaViewer({ items, index, onIndexChange, onClose, onDelete }: Props) {
  const { t } = useTranslation();
  const current = items[index];
  const { url } = useObjectUrl(current?.id ?? null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [rate, setRate] = useState(1);

  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;
  const goPrev = useCallback(() => {
    if (index > 0) onIndexChange(index - 1);
  }, [index, onIndexChange]);
  const goNext = useCallback(() => {
    if (index < items.length - 1) onIndexChange(index + 1);
  }, [index, items.length, onIndexChange]);

  // 切換媒體 → 倍速回 1x(新影片預設常速)。
  useEffect(() => {
    setRate(1);
  }, [current?.id]);

  // 套用倍速到 <video>(URL 或 rate 變更後都要重套,因為 <video> 換 src 會重置 playbackRate)。
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate, url]);

  // 鍵盤:Esc 關閉、← → 導覽。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  const cycleRate = useCallback(() => {
    setRate((r) => nextPlaybackRate(r));
  }, []);

  const download = useCallback(() => {
    if (!url || !current) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = current.filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [url, current]);

  if (!current) return null;

  return (
    <div className="gal-viewer" role="dialog" aria-modal="true" aria-label={current.filename} data-testid="gal-viewer">
      <div className="gal-viewer-bar">
        <button type="button" className="btn-ghost" onClick={onClose} aria-label={t('common.close')} data-testid="gal-viewer-close">
          ✕ {t('common.close')}
        </button>
        <span className="gal-viewer-title" title={current.filename}>
          {current.filename}
        </span>
        {current.kind === 'video' && (
          <button
            type="button"
            className="btn-ghost gal-rate"
            onClick={cycleRate}
            aria-label={t('gallery.playbackSpeed')}
            data-testid="gal-viewer-rate"
          >
            {formatRate(rate)}
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={download} data-testid="gal-viewer-download">
          {t('gallery.download')}
        </button>
        <button
          type="button"
          className="btn-ghost"
          style={{ color: 'var(--color-semantic-danger)' }}
          onClick={() => onDelete(current.id)}
          data-testid="gal-viewer-delete"
        >
          {t('gallery.delete')}
        </button>
      </div>

      <div className="gal-viewer-stage">
        <button
          type="button"
          className="gal-nav-btn gal-nav-prev"
          onClick={goPrev}
          disabled={!hasPrev}
          aria-label={t('gallery.previous')}
          data-testid="gal-viewer-prev"
        >
          ‹
        </button>

        {url ? (
          current.kind === 'video' ? (
            <video
              key={current.id}
              ref={videoRef}
              className="gal-viewer-media"
              src={url}
              controls
              autoPlay
              playsInline
              data-testid="gal-viewer-video"
            />
          ) : (
            <img key={current.id} className="gal-viewer-media" src={url} alt={current.filename} data-testid="gal-viewer-image" />
          )
        ) : (
          <span className="text-secondary">{t('common.loading')}</span>
        )}

        <button
          type="button"
          className="gal-nav-btn gal-nav-next"
          onClick={goNext}
          disabled={!hasNext}
          aria-label={t('gallery.next')}
          data-testid="gal-viewer-next"
        >
          ›
        </button>
      </div>
    </div>
  );
}
